import React, { useState } from 'react';

import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import type { MenuDay } from '@core/interfaces/api';

import {
  calculateInitialDate,
  calculateNextOccurrence,
  formatDate,
} from '../utils/date.utils';
import { MenuRowExpandedContent } from './menu-row-expanded-content';

interface MenuRowsViewProps {
  days: MenuDay[];
  cycleStartDate: string;
  daysInMenu: number;
}

function MenuRowsView({ days, cycleStartDate, daysInMenu }: MenuRowsViewProps) {
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  if (!days.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No menu days available.
      </Typography>
    );
  }

  const toggleExpand = (dayId: string) => {
    setExpandedDay(expandedDay === dayId ? null : dayId);
  };

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
            <TableCell sx={{ width: '40px', padding: '8px' }} />
            <TableCell sx={{ fontWeight: 600, padding: '8px' }}>Day</TableCell>
            <TableCell
              sx={{ fontWeight: 600, padding: '8px', whiteSpace: 'nowrap' }}
            >
              Initial Date
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, padding: '8px', whiteSpace: 'nowrap' }}
            >
              Next Occurrence
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {days.map((day) => (
            <React.Fragment key={day.id}>
              <TableRow
                hover
                onClick={() => toggleExpand(day.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={{ width: '40px', padding: '8px' }}>
                  <IconButton size="small">
                    {expandedDay === day.id ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                </TableCell>
                <TableCell
                  sx={{
                    padding: '8px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ minWidth: 0 }}
                    >
                      {day.dayOfWeek || `Day ${day.day}`}
                    </Typography>
                    <Chip
                      label={`Day ${day.day}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Week ${day.menuWeek}`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                </TableCell>
                <TableCell sx={{ padding: '8px', whiteSpace: 'nowrap' }}>
                  {formatDate(calculateInitialDate(cycleStartDate, day.day))}
                </TableCell>
                <TableCell sx={{ padding: '8px', whiteSpace: 'nowrap' }}>
                  {formatDate(
                    calculateNextOccurrence(
                      cycleStartDate,
                      day.day,
                      daysInMenu,
                    ),
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={4} sx={{ paddingY: 0 }}>
                  <Collapse
                    in={expandedDay === day.id}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box sx={{ padding: 2 }}>
                      <MenuRowExpandedContent
                        data={day.menuData}
                        showSnacks={true}
                      />
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export { MenuRowsView, type MenuRowsViewProps };
